import type { TreeNodeInfo } from '@blueprintjs/core';
import { AggregateFunction, MeasurementDimension, WindowFunction } from '@kentik/ui-shared/query/query.types';
import { isSrcDim, isDstDim } from '@kentik/ui-shared/query/query';

export interface UdeTreeNode extends TreeNodeInfo {
  childNodes?: UdeTreeNode[];
  depth?: number;
  isDirectionalNode?: boolean; // Custom property to prevent clicking without disabled styling
  _childMap?: Record<string, UdeTreeNode>;
  parentMeasurement?: string;
  measurement?: string;
  type?: 'metric' | 'dimension';
  value?: string;
  className?: string;
  nodeData?: {
    Aggregate?: AggregateFunction;
    Popular?: boolean;
    Label?: string;
    dimensions?: {
      [key: string]: MeasurementDimension;
    };
    Window?: WindowFunction;
    [key: string]: unknown;
  };
  Popular?: boolean;
}

export interface DirectionalTreeNode extends UdeTreeNode {
  isDirectionalNode: true;
  nodeData: {
    Directions: {
      [key: string]: Omit<UdeTreeNode, 'Column' | 'label' | 'id'> & { key: string };
    };
  };
}

export function updateNode(
  currentNode: UdeTreeNode,
  targetNode: UdeTreeNode,
  updater: (n: UdeTreeNode) => UdeTreeNode
): UdeTreeNode {
  if (currentNode === targetNode) {
    return updater(currentNode);
  }
  if (currentNode.childNodes) {
    return {
      ...currentNode,
      childNodes: currentNode.childNodes.map((child) => updateNode(child, targetNode, updater))
    };
  }
  return currentNode;
}

export function isSupportedMeasurement(segment: string) {
  return ['/metrics', '/traffic', '/synthetics', '/events'].some((supported) => segment.startsWith(supported));
}

export function isDirectionalDim(dimensionName: string): boolean {
  const hasSrc = isSrcDim(dimensionName);
  const hasDst = isDstDim(dimensionName);
  return hasSrc || hasDst;
}

// --- Chain Collapse ---

/** Generic category prefixes that clutter collapsed labels and should be stripped */
const COLLAPSIBLE_PREFIXES = new Set(['Devices', 'Vendors', 'NMS']);

/**
 * Basic title-casing for raw path segments that weren't already humanized.
 * Already-humanized labels (containing spaces or mixed case) pass through unchanged.
 */
function humanizeSegment(label: string): string {
  if (label === label.toLowerCase() && !label.includes(' ')) {
    return label.charAt(0).toUpperCase() + label.slice(1);
  }
  return label;
}

/**
 * Collapse chains of single-child intermediate nodes into a single node with merged labels.
 * Only collapses non-leaf intermediate nodes (nodes that have childNodes but no type/value).
 * Leaf nodes (metrics/dimensions) are never collapsed.
 *
 * Example: Devices → APC → pdu → outlet → [metrics] becomes "APC PDU Outlet" → [metrics]
 */
export function collapseChains(nodes: UdeTreeNode[]): UdeTreeNode[] {
  // eslint-disable-next-line no-use-before-define
  return nodes.map((node) => collapseNode(node));
}

function collapseNode(node: UdeTreeNode): UdeTreeNode {
  // Leaf node (metric or dimension) — no collapsing
  if (!node.childNodes || node.childNodes.length === 0 || node.type) {
    return node;
  }

  // Recursively collapse children first
  let collapsed: UdeTreeNode = { ...node, childNodes: node.childNodes.map((child) => collapseNode(child)) };

  // If this intermediate node has exactly one child and that child is also an intermediate node
  // (i.e., not a leaf metric/dimension), merge them
  while (
    collapsed.childNodes &&
    collapsed.childNodes.length === 1 &&
    collapsed.childNodes[0].childNodes &&
    collapsed.childNodes[0].childNodes.length > 0 &&
    !collapsed.childNodes[0].type // Don't absorb leaf metric/dimension nodes
  ) {
    const child = collapsed.childNodes[0];
    const parentLabel = typeof collapsed.label === 'string' ? collapsed.label : '';
    const childLabel = typeof child.label === 'string' ? child.label : '';
    const mergedLabel = [parentLabel, childLabel].filter(Boolean).map(humanizeSegment).join(' ');

    collapsed = {
      ...collapsed,
      id: child.id, // Use the deeper node's ID for tree state management
      label: mergedLabel,
      childNodes: child.childNodes,
      depth: collapsed.depth // Keep the original depth for indentation
    };
  }

  // Strip known generic prefixes from the collapsed label (e.g. "Devices APC PDU Outlet" → "APC PDU Outlet")
  if (typeof collapsed.label === 'string') {
    const labelParts = collapsed.label.split(' ');
    while (labelParts.length > 1 && COLLAPSIBLE_PREFIXES.has(labelParts[0])) {
      labelParts.shift();
    }
    collapsed = { ...collapsed, label: labelParts.join(' ') };
  }

  return collapsed;
}

// --- Fuzzy Search ---

/**
 * Compute Levenshtein edit distance between two strings.
 * Uses a single-row DP approach for O(min(a,b)) space.
 */
function editDistance(a: string, b: string): number {
  if (a === b) {
    return 0;
  }
  if (a.length === 0) {
    return b.length;
  }
  if (b.length === 0) {
    return a.length;
  }

  // Ensure a is the shorter string for space efficiency
  if (a.length > b.length) {
    [a, b] = [b, a];
  }

  let prev = Array.from({ length: a.length + 1 }, (_, i) => i);
  for (let j = 1; j <= b.length; j++) {
    const curr = [j];
    for (let i = 1; i <= a.length; i++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      curr[i] = Math.min(curr[i - 1] + 1, prev[i] + 1, prev[i - 1] + cost);
    }
    prev = curr;
  }

  return prev[a.length];
}

/**
 * Get the maximum allowed edit distance for a token based on its length.
 * Short tokens require exact matches; longer tokens allow more tolerance.
 */
function maxTypoDistance(tokenLength: number): number {
  if (tokenLength <= 3) {
    return 0;
  }
  if (tokenLength <= 6) {
    return 1;
  }
  return 2;
}

/**
 * Check if a search token fuzzy-matches anywhere in a haystack string.
 * First tries exact substring match (fast path), then falls back to
 * word-level edit distance comparison for typo tolerance.
 */
function fuzzyTokenMatch(token: string, haystack: string): boolean {
  // Fast path: exact substring match
  if (haystack.includes(token)) {
    return true;
  }

  const maxDist = maxTypoDistance(token.length);
  if (maxDist === 0) {
    return false;
  }

  // Split haystack into words and path segments for comparison
  const words = haystack.split(/[\s/]+/).filter(Boolean);
  for (const word of words) {
    // Compare against each word; also check substrings of longer words
    if (editDistance(token, word) <= maxDist) {
      return true;
    }

    // For longer haystack words, check if the token is close to any substring of similar length
    if (word.length > token.length) {
      const windowLen = token.length;
      for (let i = 0; i <= word.length - windowLen; i++) {
        const sub = word.substring(i, i + windowLen);
        if (editDistance(token, sub) <= maxDist) {
          return true;
        }
      }
      // Also try substrings one char longer (handles insertions)
      if (word.length > windowLen) {
        for (let i = 0; i <= word.length - windowLen - 1; i++) {
          const sub = word.substring(i, i + windowLen + 1);
          if (editDistance(token, sub) <= maxDist) {
            return true;
          }
        }
      }
    }
  }

  return false;
}

/**
 * Check if all search tokens fuzzy-match within a haystack string.
 * Supports typo tolerance (e.g. "interafce" matches "interface").
 */
export function fuzzyMatchAllTokens(tokens: string[], haystack: string): boolean {
  if (tokens.length === 0) {
    return false;
  }
  return tokens.every((token) => fuzzyTokenMatch(token, haystack));
}
